import { GenericModelApi } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse, NotFoundError } from "./ApiClient";
import { getTenantId } from "@utils/Auth";
import { ProviderInvoiceFormData, ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { matchesAmplifyFilter } from "@utils/Api";

export type ProviderInvoiceDataType = Partial<ApiSchema["ProviderInvoice"]["type"] & ProviderInvoiceFormData>;

class Api extends GenericModelApi<"ProviderInvoice", ProviderInvoiceDataType> {
    protected userSortKeyDateField = "fecha" as const
    /** Se crea desde el frontend, así que la etiqueta del creador se estampa aquí. */
    protected stampsCreatorUser = true


    async fetchAll(filters: any = {}, token?: string, limit: number = 20): Promise<FetchResponse<ProviderInvoiceDataType>> {
        const selectionSet = [
            ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "provider.*",
        ]

        return super.fetchAll(filters, token, limit, selectionSet);
    }

    async fetchAllForStatus(providerId: string, statusFilter: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<ProviderInvoiceDataType>> {
        const selectionSet = [
            ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "provider.*",
            "providerNotes.*",
            "providerInvoicePayments.*",
            "providerRecibe.*",
        ]
        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listProviderInvoiceByStatusProviderTenant;

        const collected: ProviderInvoiceDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                statusProviderId: {
                    beginsWith: {
                        status: statusFilter,
                        providerId: providerId
                    }
                }
            }, {
                sortDirection: "ASC",
                limit: limit,
                nextToken,
                selectionSet: selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas por tenant", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: ProviderInvoiceDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };

    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<ProviderInvoiceDataType>> {
        const selectionSet = [
            ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "provider.*",
            "providerNotes.*"
        ]

        return await super.fetchAllByPeriod("listProviderInvoiceByTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })
    }

    async getInvoiceByDocumentKey(
        documentKey: string,
        selectionSet?: any
    ): Promise<ProviderInvoiceDataType> {
        const tenantId = await getTenantId();

        const collected: ProviderInvoiceDataType[] = [];

        const fetchFunction = ApiClient.models[this.modelName].listProviderInvoiceByDocumentKeyTenant;
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            document_key: { eq: documentKey },
        }, {
            limit: 1,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener la factura del provedor", "API_ERROR", errors);
        }

        const pageItems = (data || [])
        if (pageItems.length > 0) {
            return pageItems[0]
        }

        throw new NotFoundError(`No existe ninguna factura del provedor con el codigo la clave ${documentKey} `, "API_ERROR", errors);
    }

    async getInvoiceByRecibeId(
        recibeId: string,
        selectionSet?: any
    ): Promise<ProviderInvoiceDataType[]> {
        const tenantId = await getTenantId();

        const collected: ProviderInvoiceDataType[] = [];

        const fetchFunction = ApiClient.models[this.modelName].listProviderInvoiceByRecibeIdTenant;
        const { data, errors, nextToken: nt } = await fetchFunction({
            owner: tenantId,
            providerRecibeId: { eq: recibeId },
        }, {
            limit: 500,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener la factura por numero de recibo", "API_ERROR", errors);
        }

        return (data || [])
    }

    async findByNumero(numero: string): Promise<ProviderInvoiceDataType | undefined> {
        const { items } = await this.fetchAll({ numero: { eq: numero } }, undefined, 5);
        return items[0];
    }
}
const api = new Api("ProviderInvoice", "numero", "DESC")
api.setCreateDataFieldName("fecha")
export const ProviderInvoiceApi = api